/**
 * 渠道趋势卡片组件
 * 对应板块: 板块 D — 在投广告渠道分析
 */
import React, { useState, useMemo, useCallback } from 'react';
import CardCommon from '@op/components/CardCommon';
import BubbleCommon from '@op/components/BubbleCommon';
import { lineIcon, barIcon, pieIcon, tableIcon } from '@op/constants/chart';
import { channelInfo } from '@op/components/ChannelLabel';
import ChannelTrendLineChart from './ChannelTrendLineChart';
import ChannelTrendPieChart from './ChannelTrendPieChart';
import ChannelTrendTable from './ChannelTrendTable';
import { useTimelineData } from '../../hooks/useTimelineData';
import type { IChannelTrendCardProps, TChartType } from './types';
import './index.scss';


const height = 306;

const ChannelTrendCard: React.FC<IChannelTrendCardProps> = ({
  title,
  metric,
  yAxisFormat,
  filter,
  gameInfo,
  tipsMap,
}) => {
  // ==================== 状态管理 ====================
  const [internalChartType, setInternalChartType] =
    useState<TChartType>('line');

  // ==================== 操作区配置 ====================
  // percent 类型不展示饼图和堆叠图(bar)
  const chartOperations = useMemo(() => {
    const operations = [lineIcon, barIcon, pieIcon, tableIcon];
    if (yAxisFormat === 'percent') {
      return operations.filter(
        (op) => op.symbol !== 'pie',
      );
    }
    return operations;
  }, [yAxisFormat]);

  // ==================== 下载配置 ====================
  const downloadConfig = useMemo(() => ({
    fileName: `Ad Creatives Channel ${title || metric}`,
  }), [title, metric]);

  // ==================== 数据获取 ====================
  const {
    chartData: data,
    loading,
    isEmpty,
    download,
    pieData
  } = useTimelineData({
    metric,
    filter,
    gameInfo,
    topN: 10,
    downloadConfig,
  });

  const handleChannelDownload = useCallback(() => {
    if (!download || !data?.xAxis?.length || !data?.series?.length) return;
    const columnHeaders = Object.fromEntries(
      (data.series || []).map((s) => [
        s.name,
        channelInfo(s.name as any).name || s.name,
      ]),
    );
    download({
      columnHeaders,
      valueNumFmt: yAxisFormat === 'percent' ? '0.00%' : undefined,
    });
  }, [data, download, yAxisFormat]);

  // ==================== 渲染子组件 ====================
  const renderChart = useMemo(() => {
    if (internalChartType === 'table') {
      return (
        <ChannelTrendTable
          data={data}
          loading={loading}
          valueFormat={yAxisFormat}
          height={height}
          filter={filter}
        />
      );
    }

    if (internalChartType === 'pie') {
      return (
        <ChannelTrendPieChart
          data={pieData}
          loading={loading}
          valueFormat={yAxisFormat}
          filter={filter}
          height={height}
        />
      );
    }

    return (
      <ChannelTrendLineChart
        data={data}
        chartType={internalChartType as 'line' | 'bar'}
        yAxisFormat={yAxisFormat}
        filter={filter}
        height={height}
      />
    );
  }, [internalChartType, data, loading, yAxisFormat, filter]);

  // ==================== 渲染 ====================
  // 渲染带tooltip的标题
  const renderTitle = useMemo(() => {
    if (!tipsMap) {
      return title;
    }
    return <BubbleCommon placement='top' tipsMap={tipsMap}>{title}</BubbleCommon>;
  }, [title, tipsMap]);

  return (
    <CardCommon
      title={renderTitle}
      operations={chartOperations}
      defaultOperation={internalChartType}
      handleOperationsOnClick={(item: any) => setInternalChartType(item.symbol)}
      hasDownload
      contentCard
      handleDownloadOnClick={handleChannelDownload}
      loading={loading}
      empty={isEmpty}
      emptyHeight={height}
      loadingHeight={height}
    >
      <div className="dbo-channel-trend-card">{renderChart}</div>
    </CardCommon>
  );
};

export default React.memo(ChannelTrendCard);
